import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import NumberFormat from "react-number-format";
import clsx from "clsx";

import { CButton } from "../../../../components/common/buttons";
import {
  AVR_STATUS,
  AVR_TABLE_GRID_TEMPLATE_COLUMNS,
  avrTableColumns,
} from "./AvrTable.constants";

const useStyles = makeStyles({
  wrapper: {
    width: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: "#0C4B86",
  },
  table: {
    border: "1px solid #F0F2F6",
    borderRadius: 4,
    overflow: "auto",
    backgroundColor: "#fff",
  },
  tableHead: {
    display: "grid",
    gridTemplateColumns: AVR_TABLE_GRID_TEMPLATE_COLUMNS,
    background: "#ECEEFD",
    minWidth: "100%",
  },
  tableHeadCell: {
    padding: "8px 6px",
    color: "#696C7C",
    fontSize: 12,
    fontWeight: 600,
  },
  row: {
    display: "grid",
    gridTemplateColumns: AVR_TABLE_GRID_TEMPLATE_COLUMNS,
    borderTop: "1px solid #F0F2F6",
    minWidth: "100%",
  },
  cell: {
    padding: "8px 6px",
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    borderRight: "1px solid #F0F2F6",
    "&:last-child": {
      borderRight: "none",
    },
  },
  cellCenter: {
    justifyContent: "center",
    textAlign: "center",
    flexDirection: "column",
  },
  cellRight: {
    justifyContent: "flex-end",
  },
  actionsCell: {
    flexWrap: "wrap",
    gap: 6,
    justifyContent: "center",
  },
  actionBtn: {
    padding: "4px 8px",
    minWidth: "auto",
    height: "auto",
    fontSize: 12,
  },
  remontId: {
    fontSize: 11,
    color: "#696C7C",
    marginTop: 2,
  },
  empty: {
    padding: 16,
    textAlign: "center",
    color: "#696C7C",
  },
});

const formatSum = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return (
    <NumberFormat
      value={value}
      displayType="text"
      thousandSeparator=" "
      decimalSeparator=","
      decimalScale={2}
      fixedDecimalScale
    />
  );
};

const renderActions = (row, classes, { onSign, onOpenSigned }) => {
  const id = row.contragent_avr_id;

  if (row.status_code === AVR_STATUS.CREATED) {
    return "Ожидает одобрения";
  }

  if (row.status_code === AVR_STATUS.DECLINED) {
    return "-";
  }

  if (row.status_code === AVR_STATUS.ACCEPTED) {
    return (
      <CButton className={classes.actionBtn} onClick={() => onSign?.(id)}>
        Подписать
      </CButton>
    );
  }

  if (row.status_code === AVR_STATUS.SIGNED) {
    return (
      <CButton
        className={classes.actionBtn}
        onClick={() => onOpenSigned?.(id)}
      >
        Подписанный документ
      </CButton>
    );
  }

  return "-";
};

const AvrTable = ({
  rows = [],
  remontId,
  onAddAvr,
  onSign,
  onOpenSigned,
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.header}>
        <Typography className={classes.title}>АВР</Typography>
        <CButton onClick={() => onAddAvr?.()}>Добавить АВР</CButton>
      </Box>

      <Box className={classes.table}>
        <Box className={classes.tableHead}>
          {avrTableColumns.map((col) => (
            <Box
              key={col.name}
              className={clsx(classes.tableHeadCell, {
                [classes.cellRight]: col.align === "right",
                [classes.cellCenter]: col.align === "center",
              })}
            >
              {col.label}
            </Box>
          ))}
        </Box>

        {!rows?.length ? (
          <Typography className={classes.empty}>Нет данных</Typography>
        ) : (
          rows.map((row) => (
            <Box key={row.contragent_avr_id} className={classes.row}>
              <Box className={clsx(classes.cell, classes.cellCenter)}>
                <span>{row.contragent_avr_id ?? "-"}</span>
                {(row.remont_id || remontId) && (
                  <span className={classes.remontId} title="ID ремонта">
                    {row.remont_id || remontId}
                  </span>
                )}
              </Box>
              <Box className={classes.cell}>{row.service_name || "-"}</Box>
              <Box
                className={classes.cell}
                dangerouslySetInnerHTML={{
                  __html: row.avr_name || "-",
                }}
              />
              <Box className={clsx(classes.cell, classes.cellRight)}>
                {formatSum(row.avr_sum)}
              </Box>
              <Box className={classes.cell}>{row.status_name || "-"}</Box>
              <Box className={clsx(classes.cell, classes.actionsCell)}>
                {renderActions(row, classes, {
                  onSign,
                  onOpenSigned,
                })}
              </Box>
              <Box className={classes.cell}>{row.status_1c || "-"}</Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

AvrTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  remontId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onAddAvr: PropTypes.func,
  onSign: PropTypes.func,
  onOpenSigned: PropTypes.func,
};

export default AvrTable;
